import { CALLABO_API_BASE_URL } from '@/modules/Paths';
import { getCookie, removeCookie, setCookie } from '@/utils/cookie/BrowserCookie';
import { CookieName } from '@/utils/cookie/const';
import { ACCESS_TOKEN_EXPIRED_BUFFER } from 'app/util/const';
import { LoginResponse } from 'callabo-api/src';
import { CallaboApiClient } from 'callabo-api/src/CallaboApiClient';
import { getOrCreateDeviceId } from 'libs/rtzr-commons/FingerprintUtils';
import { IStorage } from 'libs/rtzr-commons/IStorage';
import { MemoryCache } from 'libs/rtzr-commons/MemoryCache';
import { ApiError } from 'next/dist/server/api-utils';

/**
 * 인증 토큰 관리
 * indexed db를 활용하여, 각 탭과 worker에서의 토큰을 동기화한다.
 */
export class AuthTokenStorage {
  private _isRefreshing = false;
  public get isRefreshing(): boolean {
    return this._isRefreshing;
  }

  // auth 만료 플래그
  private _isAuthDenied = false;
  public get isAuthDenied(): boolean {
    return this._isAuthDenied;
  }

  public denyAuth = (): void => {
    this._isAuthDenied = true;
  };

  public getAccessTokenAsync = (): Promise<string> => {
    return this._database.getAsync('access_token', true);
  };

  private setAccessTokenAsync = (accessToken: string, expires?: number): Promise<void> => {
    return this._database.setAsync('access_token', accessToken, expires);
  };

  public getRefreshTokenAsync = (): Promise<string> => {
    return this._database.getAsync('refresh_token', true);
  };

  public setRefreshTokenAsync = (refreshToken: string, expires?: number): Promise<void> => {
    return this._database.setAsync('refresh_token', refreshToken, expires);
  };

  public isExistTokenAsync = async (): Promise<boolean> => {
    return !!(await this.getAccessTokenAsync()) && !!(await this.getRefreshTokenAsync());
  };

  public setAccessTokenExpiredDate = (tokenExpiredDate: Date, expires?: number): Promise<void> => {
    return this._database.setAsync('token_expired_date', tokenExpiredDate.toISOString(), expires);
  };

  public getAccessTokenExpiredDate = (): Promise<string> => {
    return this._database.getAsync('token_expired_date', true);
  };

  private constructor(private readonly _database: IStorage) {}

  /**
   * 토큰을 재갱신한다.
   */
  public refreshAsync = async (): Promise<LoginResponse> => {
    // console.log('refreshAsync');
    const apiClientWithoutToken = new CallaboApiClient({
      BASE: CALLABO_API_BASE_URL,
    });
    if (this._isRefreshing) return;

    this._isRefreshing = true;
    // console.trace('_isRefreshing true');
    try {
      const refreshToken = await this.getRefreshTokenAsync();
      if (!refreshToken) {
        throw new ApiError(401, 'Unauthorized. Please login again.');
      }
      const deviceId = getOrCreateDeviceId();
      const authRes = (await apiClientWithoutToken.user.authV1UserAuthPost({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        device_id: deviceId,
      })) as LoginResponse; // 250428 기준 2FA 작업에서는 명시적으로 로그인을 시도했을 때만 이메일 2FA를 하도록 함
      const expires = Math.round(authRes.token.expires_in * ACCESS_TOKEN_EXPIRED_BUFFER);
      await this.setAccessTokenAsync(authRes.token.access_token, expires);
      await this.setRefreshTokenAsync(authRes.token.refresh_token, 30 * 24 * 60 * 60);
      const tokenCreateAt = authRes.token.created_at;
      const tokenExpiredDate = new Date(tokenCreateAt);
      tokenExpiredDate.setSeconds(tokenExpiredDate.getSeconds() + expires);
      await this.setAccessTokenExpiredDate(tokenExpiredDate, expires);
      return authRes as LoginResponse;
      // } catch (e) {
      //     console.log(`refresh중 에러 발생 ${e}`);
    } finally {
      this._isRefreshing = false;
    }
  };

  public close = (): void => {
    this._database.removeMany(['access_token', 'refresh_token', 'token_expired_date']);
  };

  public static createAsync = async (): Promise<AuthTokenStorage> => {
    return new AuthTokenStorage(CookieStorage);
  };

  public static createForShareAsync = async (
    accessToken: string,
    refreshToken: string,
    tokenExpiredDate: Date
  ): Promise<AuthTokenStorage> => {
    const memoryCache = new MemoryCache();
    await memoryCache.setAsync('access_token', accessToken);
    await memoryCache.setAsync('refresh_token', refreshToken);
    await memoryCache.setAsync('token_expired_date', tokenExpiredDate.toISOString());
    return new AuthTokenStorage(memoryCache);
  };
}

const CookieStorage: IStorage<CookieName, string> = {
  getAsync: async (key: CookieName) => {
    return getCookie(key);
  },
  setAsync: async (key: CookieName, data: string, expires?: number) => {
    setCookie(key, data, expires);
  },
  removeAsync: async (key: CookieName) => {
    removeCookie(key);
  },
  removeMany: async (keys: CookieName[]) => {
    keys.forEach((key) => removeCookie(key));
  },
};
